import { useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Sparkles, X } from "lucide-react";

import {
  ALARM_RULE_TEMPLATES,
  type RuleTemplate,
} from "@/data/alarmRules/templates";
import type { AlarmTargetableDevice } from "@/data/adapter";
import { cn } from "@/lib/cn";

interface Props {
  /**
   * Pre-selected target devices (the operator can edit before applying).
   * Templates resolve their datapoint suggestions against the first
   * target's topic globs.
   */
  defaultTargets: AlarmTargetableDevice[];
  onPick: (template: RuleTemplate, targets: AlarmTargetableDevice[]) => void;
}

const CATEGORY_TONE: Record<RuleTemplate["category"], string> = {
  safety: "border-critical/40 bg-critical/10 text-critical",
  communications: "border-accent/40 bg-accent/10 text-accent",
  operations: "border-warn/40 bg-warn/10 text-warn",
  performance: "border-edge bg-surface-2/40 text-text-faint",
};

export function TemplatesGallery({ defaultTargets, onPick }: Props) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<RuleTemplate["category"] | "all">("all");
  const [targets, setTargets] = useState<AlarmTargetableDevice[]>(defaultTargets);

  const visible = useMemo(
    () =>
      filter === "all"
        ? ALARM_RULE_TEMPLATES
        : ALARM_RULE_TEMPLATES.filter((t) => t.category === filter),
    [filter],
  );

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <button
          type="button"
          onClick={() => setTargets(defaultTargets)}
          className="text-[11px] uppercase tracking-wide px-3 py-1.5 rounded border border-edge hover:bg-surface-2/60 flex items-center gap-1"
        >
          <Sparkles className="size-3.5" /> Templates
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-40" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(900px,calc(100vw-2rem))] max-h-[80vh] z-50 bg-surface border border-edge rounded-md shadow-2xl flex flex-col">
          <div className="flex items-center justify-between border-b border-edge px-4 py-2.5">
            <Dialog.Title className="text-[13px] font-semibold">
              Starter templates
            </Dialog.Title>
            <Dialog.Close asChild>
              <button type="button" aria-label="Close" className="text-text-faint hover:text-text">
                <X className="size-4" />
              </button>
            </Dialog.Close>
          </div>

          <div className="border-b border-edge px-4 py-2 flex flex-wrap items-center gap-2">
            <span className="text-[11px] uppercase tracking-wider text-text-faint">
              Filter
            </span>
            {(["all", "safety", "communications", "operations", "performance"] as const).map(
              (c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setFilter(c)}
                  className={cn(
                    "text-[11px] uppercase tracking-wide px-2 py-1 rounded border",
                    filter === c
                      ? "border-accent/60 bg-accent/15 text-accent"
                      : "border-edge bg-surface-2/40 text-text-faint hover:text-text",
                  )}
                >
                  {c}
                </button>
              ),
            )}
            <div className="ml-auto text-[11px] text-text-faint">
              Applies to {targets.length} device{targets.length === 1 ? "" : "s"}.
            </div>
          </div>

          <div className="flex-1 overflow-auto grid grid-cols-2 gap-3 p-4">
            {visible.map((tpl) => (
              <button
                type="button"
                key={tpl.key}
                onClick={() => {
                  setOpen(false);
                  onPick(tpl, targets);
                }}
                className="text-left border border-edge rounded-md p-3 hover:bg-surface-2/60 flex flex-col gap-2"
              >
                <div className="flex items-center gap-2">
                  <span className="text-[12px] font-semibold flex-1">{tpl.name}</span>
                  <span
                    className={cn(
                      "text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border font-semibold",
                      CATEGORY_TONE[tpl.category],
                    )}
                  >
                    {tpl.category}
                  </span>
                </div>
                <p className="text-[11px] text-text-faint">{tpl.description}</p>
              </button>
            ))}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
